import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, X, Pin, PinOff, Pencil, Trash2, Send } from "lucide-react";
import { photoSrc, fmtPostDate } from "../PostCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full px-3 py-2 border-2 border-[#3D2008] bg-white font-manrope text-sm text-[#3D2008] focus:outline-none focus:border-[#C4522A]";
const EMPTY = { text: "", photos: [], link_url: "", link_label: "" };

const Composer = ({ adminKey, initial, onDone, onCancel }) => {
    const [form, setForm] = useState(initial || EMPTY);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef();
    const h = { "x-admin-password": adminKey };

    const addPhotos = async (files) => {
        const room = 10 - form.photos.length;
        const list = Array.from(files).slice(0, room);
        if (Array.from(files).length > room) toast.error("A post can have up to 10 photos.");
        setBusy(true);
        try {
            for (const f of list) {
                const fd = new FormData(); fd.append("file", f);
                const r = await axios.post(`${API}/admin/upload`, fd, { headers: h });
                setForm(p => ({ ...p, photos: [...p.photos, r.data.url] }));
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Photo upload failed."); }
        finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const submit = async () => {
        if (!form.text.trim()) { toast.error("Write something first."); return; }
        setBusy(true);
        try {
            if (initial?.id) await axios.put(`${API}/admin/posts/${initial.id}`, form, { headers: h });
            else await axios.post(`${API}/admin/posts`, form, { headers: h });
            toast.success(initial?.id ? "Post updated." : "Posted! It's live on What's Happening.");
            setForm(EMPTY); onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save the post."); }
        finally { setBusy(false); }
    };

    return (
        <div className="bg-white border-2 border-[#3D2008] p-4 sm:p-5 shadow-[4px_4px_0px_0px_rgba(61,32,8,0.2)]" data-testid={initial?.id ? "post-editor" : "post-composer"}>
            <textarea value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} rows={4} maxLength={5000}
                placeholder="What's happening? Bingo this Saturday, a big thank-you, build news…" data-testid="post-text-input"
                className={`${inputCls} text-base resize-y min-h-[110px]`} />
            {form.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="post-photo-previews">
                    {form.photos.map((p, i) => (
                        <div key={p} className="relative w-20 h-20 border-2 border-[#3D2008]">
                            <img src={photoSrc(p)} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => setForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} aria-label="Remove photo"
                                data-testid={`post-photo-remove-${i}`} className="absolute -top-2 -right-2 w-6 h-6 bg-[#C4522A] text-white flex items-center justify-center border border-[#3D2008]"><X size={12} /></button>
                        </div>
                    ))}
                </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} placeholder="Optional link (e.g. ticket page)" data-testid="post-link-input" className={inputCls} />
                <input value={form.link_label} onChange={e => setForm({ ...form, link_label: e.target.value })} maxLength={60} placeholder="Button label (e.g. Get bingo tickets)" data-testid="post-link-label-input" className={inputCls} />
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => addPhotos(e.target.files)} data-testid="post-photo-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy || form.photos.length >= 10} data-testid="post-add-photos-btn"
                    className="inline-flex items-center gap-2 h-11 px-4 bg-white text-[#3D2008] border-2 border-[#3D2008] font-manrope font-bold text-sm hover:bg-[#F5EDD6] disabled:opacity-50">
                    <ImagePlus size={16} /> {busy ? "Uploading…" : `Add photos${form.photos.length ? ` (${form.photos.length}/10)` : ""}`}
                </button>
                <div className="flex-1" />
                {onCancel && <button type="button" onClick={onCancel} className="h-11 px-4 font-manrope text-sm text-[#5C4A35] underline">Cancel</button>}
                <button type="button" onClick={submit} disabled={busy} data-testid="post-submit-btn"
                    className="inline-flex items-center gap-2 h-11 px-6 bg-[#C4522A] text-white border-2 border-[#3D2008] font-anton uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none disabled:opacity-60">
                    <Send size={15} /> {initial?.id ? "Save" : "Post"}
                </button>
            </div>
        </div>
    );
};

export const PostsTab = ({ adminKey }) => {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null);
    const h = { "x-admin-password": adminKey };
    const load = () => axios.get(`${API}/posts`, { params: { limit: 50 } }).then(r => setPosts(r.data.posts)).catch(() => {});
    useEffect(() => { load(); }, []);

    const pin = async (p) => {
        try { const r = await axios.post(`${API}/admin/posts/${p.id}/pin`, {}, { headers: h }); toast.success(r.data.pinned ? "Pinned to the top." : "Unpinned."); load(); }
        catch { toast.error("Could not update pin."); }
    };
    const del = async (p) => {
        if (!window.confirm("Delete this post? This can't be undone.")) return;
        try { await axios.delete(`${API}/admin/posts/${p.id}`, { headers: h }); toast.success("Post deleted."); load(); }
        catch { toast.error("Could not delete."); }
    };

    return (
        <div data-testid="posts-tab" className="max-w-3xl">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-1">What's Happening</h2>
            <p className="font-manrope text-sm text-[#5C4A35] mb-5">Post news, thank-yous and photos. It goes live on the site right away.</p>
            {!editing && <Composer adminKey={adminKey} onDone={load} />}
            <div className="mt-8 space-y-4">
                {posts.map((p, i) => (
                    <div key={p.id} data-testid={`admin-post-${i}`} className="bg-[#F5EDD6] border-2 border-[#3D2008] p-4">
                        {editing?.id === p.id ? (
                            <Composer adminKey={adminKey} initial={{ id: p.id, text: p.text, photos: p.photos || [], link_url: p.link_url || "", link_label: p.link_label || "" }}
                                onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
                        ) : (
                            <>
                                <div className="flex items-start justify-between gap-3">
                                    <time className="font-manrope text-xs text-[#8A7A65]">{fmtPostDate(p.created_at)}{p.updated_at !== p.created_at ? " · edited" : ""}</time>
                                    {p.pinned && <span className="inline-flex items-center gap-1 bg-[#C9A84C] text-[#3D2008] text-[11px] font-bold uppercase px-2 py-0.5"><Pin size={11} /> Pinned</span>}
                                </div>
                                <p className="font-manrope text-sm text-[#3D2008] whitespace-pre-wrap mt-2 line-clamp-4">{p.text}</p>
                                {p.photos?.length > 0 && (
                                    <div className="flex gap-1.5 mt-2">{p.photos.slice(0, 5).map(ph => <img key={ph} src={photoSrc(ph)} alt="" className="w-14 h-14 object-cover border border-[#3D2008]" />)}{p.photos.length > 5 && <span className="font-manrope text-xs text-[#5C4A35] self-center">+{p.photos.length - 5}</span>}</div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <button onClick={() => setEditing(p)} data-testid={`admin-post-edit-${i}`} className="inline-flex items-center gap-1 h-9 px-3 border-2 border-[#3D2008] bg-white text-[#3D2008] text-xs font-bold uppercase"><Pencil size={12} /> Edit</button>
                                    <button onClick={() => pin(p)} data-testid={`admin-post-pin-${i}`} className="inline-flex items-center gap-1 h-9 px-3 border-2 border-[#3D2008] bg-white text-[#3D2008] text-xs font-bold uppercase">{p.pinned ? <><PinOff size={12} /> Unpin</> : <><Pin size={12} /> Pin</>}</button>
                                    <button onClick={() => del(p)} data-testid={`admin-post-delete-${i}`} className="inline-flex items-center gap-1 h-9 px-3 border-2 border-red-700 text-red-700 bg-white text-xs font-bold uppercase hover:bg-red-700 hover:text-white"><Trash2 size={12} /> Delete</button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
                {posts.length === 0 && <p className="font-manrope text-sm text-[#8A7A65] text-center py-8">No posts yet — write the first one above.</p>}
            </div>
        </div>
    );
};
